import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X, Globe, Instagram, Facebook, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const navLinks = [
    { path: '/', label: t('Accueil', 'Startseite') },
    { path: '/about', label: t('Qui suis-je', 'Über mich') },
    { path: '/experience', label: t('Mon expérience', 'Meine Erfahrung') },
    { path: '/tips', label: t('Conseils', 'Tipps') },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: t('Contact', 'Kontakt') },
  ];

  const isActive = (path) => location.pathname === path;

  const siteName = t('Un billet, une valise', 'Ein Ticket, ein Koffer');

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isHome ? 'bg-transparent' : 'glass border-b border-slate-200/50'
    }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="sr-only"
            data-testid="navbar-logo"
          >
            {siteName}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'bg-blue-600 text-white'
                    : isHome
                      ? 'text-white/90 hover:bg-white/20 hover:text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex items-center gap-2 text-sm font-medium ${
                isHome ? 'text-white hover:bg-white/20 hover:text-white' : ''
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </Button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isHome ? 'text-white hover:bg-white/20' : 'hover:bg-slate-100'
              }`}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className={`lg:hidden pb-6 animate-fadeIn ${isHome ? 'bg-black/40 backdrop-blur-md rounded-xl p-4 -mx-2' : ''}`}>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? 'bg-blue-600 text-white'
                      : isHome
                        ? 'text-white hover:bg-white/20'
                        : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await axios.get(`${API}/social-links`);
        setSocialLinks(response.data);
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const siteNameFull = t(
    "Un billet, une valise et l'Allemagne",
    "Ein Ticket, ein Koffer und Deutschland"
  );

  const getIconComponent = (platform) => {
    const name = platform.toLowerCase();
    if (name.includes('instagram')) return Instagram;
    if (name.includes('facebook')) return Facebook;
    return null;
  };

  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="text-xl font-bold font-outfit mb-4">
              {siteNameFull}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t(
                "Un blog personnel sur l'aventure de vivre en Allemagne.",
                "Ein persönlicher Blog über das Abenteuer, in Deutschland zu leben."
              )}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('Navigation', 'Navigation')}</h4>
            <div className="flex flex-col gap-2">
              <Link to="/about" className="text-slate-400 hover:text-white transition-colors text-sm">
                {t('Qui suis-je', 'Über mich')}
              </Link>
              <Link to="/blog" className="text-slate-400 hover:text-white transition-colors text-sm">
                Blog
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white transition-colors text-sm">
                {t('Contact', 'Kontakt')}
              </Link>
            </div>
          </div>

          {/* Legal + Social */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('Informations', 'Informationen')}</h4>
            <div className="flex flex-col gap-2 mb-6">
              <Link to="/mentions-legales" className="text-slate-400 hover:text-white transition-colors text-sm">
                {t('Mentions légales', 'Rechtliche Hinweise')}
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors text-sm">
                {t('Politique de confidentialité', 'Datenschutz')}
              </Link>
              <Link to="/impressum" className="text-slate-400 hover:text-white transition-colors text-sm">
                Impressum
              </Link>
              <Link to="/admin" className="text-slate-400 hover:text-white transition-colors text-sm">
                Admin
              </Link>
            </div>
            <div className="flex gap-4">
              {socialLinks.length > 0 ? (
                socialLinks.map((link) => {
                  const IconComponent = getIconComponent(link.platform);
                  return IconComponent ? (
                    <a 
                      key={link.id}
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 bg-slate-800 rounded-full hover:bg-blue-600 transition-colors"
                      data-testid={`footer-${link.platform.toLowerCase()}`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  ) : null;
                })
              ) : (
                <>
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 bg-slate-800 rounded-full hover:bg-blue-600 transition-colors"
                    data-testid="footer-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 bg-slate-800 rounded-full hover:bg-blue-600 transition-colors"
                    data-testid="footer-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 – {siteNameFull}
          </p>
          <button
            onClick={scrollToTop}
            className="p-2 bg-slate-800 rounded-full hover:bg-blue-600 transition-colors"
            data-testid="scroll-to-top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children, className = "", fullScreen = false }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className={`flex-1 ${fullScreen ? '' : 'pt-16 md:pt-20'} ${className}`}>
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
